/**
 * Lazy-loads accordion-custom for FAQ sections (IO + idle warm).
 * Used by snippets/faq-accordion-lazy-load.liquid.
 */

/**
 * @param {boolean} isMobile
 * @returns {string} IntersectionObserver rootMargin
 */
export function resolveFaqAccordionRootMargin(isMobile) {
  return isMobile ? '300px' : '1000px'
}

/**
 * @param {() => Promise<unknown> | unknown} importModule
 * @returns {() => Promise<unknown> | unknown | void}
 */
export function createImportOnce(importModule) {
  let imported = false
  return () => {
    if (imported) return
    imported = true
    return importModule()
  }
}

/**
 * Opens the FAQ `<details>` targeted by a URL hash (matching the `<details>` id,
 * its question/answer ids, or any descendant inside `.faq-section`), closes other
 * open siblings in the same `[data-accordion-group]`, and scrolls it into view.
 *
 * @param {ParentNode} [root]
 * @param {string} [hash]
 * @param {object} [options]
 * @param {() => void} [options.onMatch]
 * @param {boolean} [options.scroll]
 * @returns {HTMLDetailsElement | null}
 */
export function openFaqTargetForHash(
  root = document,
  hash = typeof window !== 'undefined' ? window.location.hash : '',
  { onMatch, scroll = true } = {}
) {
  if (!root || !hash || !hash.startsWith('#')) return null

  let id
  try {
    id = decodeURIComponent(hash.slice(1)).trim()
  } catch {
    id = hash.slice(1).trim()
  }
  if (!id) return null

  const target =
    typeof (/** @type {Document} */ (root).getElementById) === 'function'
      ? /** @type {Document} */ (root).getElementById(id)
      : root.querySelector(`#${CSS.escape(id)}`)

  if (!(target instanceof Element)) return null

  const details = target.matches('details.faq__details')
    ? target
    : target.closest('details.faq__details')

  if (!(details instanceof HTMLDetailsElement)) return null

  onMatch?.()

  const group = details.closest('[data-accordion-group]')
  const section = details.closest('.faq-section')
  const parent = details.parentElement
  const motionReady =
    section instanceof HTMLElement &&
    section.hasAttribute('data-faq-motion') &&
    parent instanceof HTMLElement &&
    typeof parent.openAnimated === 'function'

  if (motionReady) {
    parent.openAnimated()
  } else if (group) {
    const openSiblings = group.querySelectorAll(
      'accordion-custom[single-open] > details[open], details.faq__details[open]'
    )
    for (const sibling of openSiblings) {
      if (sibling === details || !(sibling instanceof HTMLDetailsElement)) {
        continue
      }
      sibling.removeAttribute('data-closing')
      sibling.open = false
    }

    details.removeAttribute('data-closing')
    details.open = true
  } else {
    details.removeAttribute('data-closing')
    details.open = true
  }

  if (scroll && typeof details.scrollIntoView === 'function') {
    const reducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    details.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'nearest'
    })
  }

  return details
}

/**
 * Schedule FAQ accordion module load: idle warm + near-viewport IO + hash deep-link.
 *
 * @param {object} options
 * @param {() => Promise<unknown> | unknown} options.importModule
 * @param {ParentNode} [options.root]
 * @param {(query: string) => { matches: boolean }} [options.matchMedia]
 * @param {(cb: () => void, opts?: { timeout: number }) => number} [options.requestIdleCallback]
 * @param {typeof IntersectionObserver} [options.IntersectionObserverCtor]
 * @param {number} [options.idleTimeout]
 * @returns {{ importOnce: () => unknown, disconnect: () => void }}
 */
export function scheduleFaqAccordionLazyLoad({
  importModule,
  root = document,
  matchMedia = window.matchMedia.bind(window),
  requestIdleCallback: requestIdle,
  IntersectionObserverCtor = window.IntersectionObserver,
  idleTimeout = 2500
} = {}) {
  const importOnce = createImportOnce(importModule)
  let idleId = 0
  let timeoutId = 0
  /** @type {IntersectionObserver | null} */
  let observer = null

  const targets = root.querySelectorAll(
    '.faq-section:not([data-faq-accordion-lazy-observed])'
  )

  if (targets.length === 0) {
    return {
      importOnce,
      disconnect() {}
    }
  }

  let motionFrame = 0
  const markMotion = () => {
    motionFrame = 0
    for (const target of targets) {
      if (target instanceof HTMLElement)
        target.setAttribute('data-faq-motion', '')
    }
  }

  if (typeof requestAnimationFrame === 'function') {
    motionFrame = requestAnimationFrame(markMotion)
  } else {
    markMotion()
  }

  const handleHashChange = () => {
    if (typeof window === 'undefined') return
    openFaqTargetForHash(root, window.location.hash, { onMatch: importOnce })
  }

  if (typeof window !== 'undefined') {
    if (window.location?.hash) {
      openFaqTargetForHash(root, window.location.hash, { onMatch: importOnce })
    }
    window.addEventListener('hashchange', handleHashChange)
  }

  const scheduleIdle =
    typeof requestIdle === 'function'
      ? (cb) => {
          idleId = requestIdle(cb, { timeout: idleTimeout })
        }
      : typeof window !== 'undefined' &&
          typeof window.requestIdleCallback === 'function'
        ? (cb) => {
            idleId = window.requestIdleCallback(cb, { timeout: idleTimeout })
          }
        : (cb) => {
            timeoutId = setTimeout(cb, 1)
          }

  // Idle warm so a fast scroll+click does not race module compile (desktop INP).
  scheduleIdle(importOnce)

  if (typeof IntersectionObserverCtor !== 'function') {
    importOnce()
    return {
      importOnce,
      disconnect() {
        if (motionFrame && typeof cancelAnimationFrame === 'function') {
          cancelAnimationFrame(motionFrame)
          motionFrame = 0
        }
        if (typeof window !== 'undefined') {
          window.removeEventListener('hashchange', handleHashChange)
        }
        if (
          idleId &&
          typeof window !== 'undefined' &&
          window.cancelIdleCallback
        ) {
          window.cancelIdleCallback(idleId)
        }
        if (timeoutId) clearTimeout(timeoutId)
      }
    }
  }

  const isMobile = matchMedia('(max-width: 749px)').matches
  const rootMargin = resolveFaqAccordionRootMargin(isMobile)

  observer = new IntersectionObserverCtor(
    (entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          obs.disconnect()
          observer = null
          importOnce()
          break
        }
      }
    },
    { rootMargin }
  )

  targets.forEach((target) => {
    target.setAttribute('data-faq-accordion-lazy-observed', '')
    observer?.observe(target)
  })

  return {
    importOnce,
    disconnect() {
      if (motionFrame && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(motionFrame)
        motionFrame = 0
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('hashchange', handleHashChange)
      }
      observer?.disconnect()
      observer = null
      if (
        idleId &&
        typeof window !== 'undefined' &&
        window.cancelIdleCallback
      ) {
        window.cancelIdleCallback(idleId)
      }
      if (timeoutId) clearTimeout(timeoutId)
    }
  }
}

const bootScript = document.querySelector('script[data-faq-accordion-lazy]')
const accordionUrl = bootScript?.dataset?.accordionUrl
if (accordionUrl) {
  scheduleFaqAccordionLazyLoad({
    importModule: () => import(/* @vite-ignore */ accordionUrl)
  })
}
